// ListarProdutos.js - Componente de Listagem de Produtos
// ✅ Tabela com todos os campos
// ✅ Filtros avançados
// ✅ Busca por nome, código, descrição
// ✅ Paginação
// ✅ Formatação de moeda e números
// ✅ Popup para Adicionar/Editar Produto (igual ao Produtos.js - com 3 tabs)
// ✅ Gráficos com Recharts
// ✅ Stock Atual BLOQUEADO na edição
// ✅ Ordenação por Data de Criação (mais recentes primeiro)
// ✅ SEM DATA DE ENTRADA no TAB de Stock (gerada automaticamente)
// 🔥 IVA CORRIGIDO: SUPORTA 0%, NUNCA USA FALLBACK 16
// 🔥 PRECO DE VENDA CALCULADO COM IVA EM TEMPO REAL (como no Produtos.js)
// 🔥 GRÁFICOS MIGRADOS DE chart.js → recharts
// 🔥 LAYOUT ORGANIZADO: grelha de gráficos 2 colunas + tabela com scroll horizontal

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import './ListarProdutos.css';
import { Icon } from './SharedComponents';

// ==============================================
// GRÁFICOS (Recharts)
// ==============================================
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const API_BASE_URL = '/api';

// ==============================================
// CONFIGURAÇÕES PADRÃO (fallback)
// ==============================================
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.'
    },
    formatoData: 'DD/MM/YYYY',
    timezone: 'Africa/Maputo',
    idioma: 'pt-PT',
    iva: null
};

// ==============================================
// CONSTANTES
// ==============================================
const UNIDADES_MEDIDA = [
    { value: 'UN', label: 'Unidade' },
    { value: 'KG', label: 'Quilograma' },
    { value: 'L', label: 'Litro' },
    { value: 'M', label: 'Metro' },
    { value: 'CX', label: 'Caixa' },
    { value: 'PCT', label: 'Pacote' }
];

const ITEMS_PER_PAGE = 5;

// ==============================================
// CORES PARA GRÁFICOS
// ==============================================
const CORES_GRAFICOS = [
    '#5c6bc0', '#10b981', '#f59e0b', '#dc2626', '#3b82f6',
    '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'
];

// 🔥 Cores fixas para o gráfico de status de stock
const CORES_STATUS_STOCK = ['#dc2626', '#f59e0b', '#3b82f6', '#10b981'];

export default function ListarProdutos() {
    // ==============================================
    // ESTADOS PRINCIPAIS
    // ==============================================
    const [produtos, setProdutos] = useState([]);
    const [filteredProdutos, setFilteredProdutos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [configEmpresa, setConfigEmpresa] = useState(CONFIG_PADRAO);

    // ==============================================
    // FILTROS E PESQUISA
    // ==============================================
    const [searchTerm, setSearchTerm] = useState('');
    const [filtros, setFiltros] = useState({
        categoria: 'todos',
        fornecedor: 'todos',
        statusStock: 'todos',
        unidadeMedida: 'todos',
        ativo: 'todos'
    });
    const [showFiltros, setShowFiltros] = useState(false);

    // ==============================================
    // PAGINAÇÃO
    // ==============================================
    const [pagination, setPagination] = useState({
        page: 1,
        limit: ITEMS_PER_PAGE,
        total: 0,
        pages: 0
    });

    // ==============================================
    // DADOS AUXILIARES
    // ==============================================
    const [categorias, setCategorias] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);
    const [loggedUser, setLoggedUser] = useState(null);
    const [empresaId, setEmpresaId] = useState(null);

    // ==============================================
    // ESTADOS DE CARREGAMENTO
    // ==============================================
    const [exportLoading, setExportLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // ==============================================
    // MODAL - ADICIONAR/EDITAR
    // ==============================================
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [createMessage, setCreateMessage] = useState({ text: '', type: '' });
    const [editMessage, setEditMessage] = useState({ text: '', type: '' });
    const [editFormData, setEditFormData] = useState(null);
    const [activeTab, setActiveTab] = useState('geral');
    const [editActiveTab, setEditActiveTab] = useState('geral');

    // ==============================================
    // 🔥 ESTADOS PARA ENTRADA DE STOCK NO MODAL DE CRIAÇÃO (SEM DATA)
    // ==============================================
    const [stockFormData, setStockFormData] = useState({
        quantidade: 1,
        precoUnitario: 0,
        fornecedorId: '',
        notaFiscal: '',
        observacao: ''
    });

    // ==============================================
    // FORM DATA - CRIAÇÃO
    // ==============================================
    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        codigo: '',
        precoUnitario: '',
        precoVenda: '',
        precoCompra: '',
        compraQuantidade: 0,
        stockAtual: 0,
        stockMinimo: 0,
        unidadeMedida: 'UN',
        categoriaId: '',
        fornecedorId: '',
        ativo: true,
        impostos: {
            iva: null,
            ispc: 0,
            usarIvaEmpresa: true
        }
    });

    // ==============================================
    // FORM DATA - EDIÇÃO
    // ==============================================
    const [editFormDataState, setEditFormDataState] = useState(null);

    // ==============================================
    // API INSTANCE
    // ==============================================
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache'
        }
    });

    // ==============================================
    // FUNÇÕES AUXILIARES
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const obterEmpresaIdDoUtilizador = (user) => {
        if (!user) return null;
        let id = extrairEmpresaId(user.empresaId);
        if (id) return id;
        id = extrairEmpresaId(user.empresa);
        if (id) return id;
        id = extrairEmpresaId(user.empresaAtual);
        if (id) return id;
        if (user.empresas && Array.isArray(user.empresas) && user.empresas.length > 0) {
            id = extrairEmpresaId(user.empresas[0]);
            if (id) return id;
        }
        return null;
    };

    const getStatusStock = (stock, stockMinimo = 0) => {
        if (stock === 0) return { label: 'Sem Stock', color: '#dc2626', icon: 'block', class: 'sem-stock' };
        if (stock <= stockMinimo) return { label: 'Stock Baixo', color: '#f59e0b', icon: 'warning', class: 'baixo-stock' };
        if (stock < 10) return { label: 'Stock Médio', color: '#3b82f6', icon: 'info', class: 'stock-medio' };
        return { label: 'Stock Alto', color: '#10b981', icon: 'check_circle', class: 'stock-alto' };
    };

    const getUnidadeLabel = (value) => {
        const found = UNIDADES_MEDIDA.find(u => u.value === value);
        return found ? found.label : value || '-';
    };

    const formatarMoeda = (valor) => {
        const moeda = configEmpresa?.moeda || CONFIG_PADRAO.moeda;
        const { simbolo = 'MT', casasDecimais = 2, separadorDecimal = ',', separadorMilhar = '.' } = moeda;

        if (valor === undefined || valor === null || isNaN(valor)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        let numero = Number(valor);
        if (isNaN(numero)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        numero = numero.toFixed(casasDecimais);
        const partes = numero.split('.');
        let inteiros = partes[0];
        const decimais = partes[1] || '0'.repeat(casasDecimais);

        inteiros = inteiros.replace(/\B(?=(\d{3})+(?!\d))/g, separadorMilhar);

        return `${inteiros}${separadorDecimal}${decimais} ${simbolo}`;
    };

    const formatarNumero = (valor) => {
        if (valor === undefined || valor === null) return '0';
        const num = Number(valor);
        if (isNaN(num)) return '0';

        if (num >= 1e9) {
            return (num / 1e9).toFixed(1) + 'B';
        }
        if (num >= 1e6) {
            return (num / 1e6).toFixed(1) + 'M';
        }
        if (num >= 1e3) {
            return (num / 1e3).toFixed(1) + 'k';
        }

        return num.toLocaleString('pt-PT');
    };

    const showTempToast = (title, message, type = 'success') => {
        setMessage({ text: `${title}: ${message}`, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    // ==============================================
    // 🔥 FUNÇÕES DE CÁLCULO DE PREÇO/IVA
    // ==============================================
    const calcularPrecoComIVA = (precoSemIVA, iva) => {
        if (!precoSemIVA || precoSemIVA <= 0) return null;
        if (iva === null || iva === undefined || iva === 0) return precoSemIVA;
        const taxaIVA = Number(iva) / 100;
        return precoSemIVA * (1 + taxaIVA);
    };

    const calcularPrecoSemIVA = (precoComIVA, iva) => {
        if (!precoComIVA || precoComIVA <= 0) return null;
        if (iva === null || iva === undefined || iva === 0) return precoComIVA;
        const taxaIVA = Number(iva) / 100;
        return precoComIVA / (1 + taxaIVA);
    };

    const getIvaEfetivo = (impostos) => {
        if (!impostos) return null;

        if (impostos.usarIvaEmpresa) {
            return configEmpresa?.iva !== null && configEmpresa?.iva !== undefined ? configEmpresa.iva : null;
        }

        return impostos.iva !== null && impostos.iva !== undefined ? impostos.iva : null;
    };

    const sanitizarPreco = (valor) => {
        if (!valor) return '';
        let val = valor.replace(/[^0-9.]/g, '');
        const partes = val.split('.');
        if (partes.length > 2) {
            val = partes[0] + '.' + partes.slice(1).join('');
        }
        if (val.startsWith('.')) {
            val = '0' + val;
        }
        return val;
    };

    // ==============================================
    // 🔥 FUNÇÃO PARA ORDENAR PRODUTOS (MAIS RECENTES PRIMEIRO)
    // ==============================================
    const ordenarProdutos = (lista) => {
        return [...lista].sort((a, b) => {
            const dataA = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const dataB = b.createdAt ? new Date(b.createdAt) : new Date(0);
            return dataB.getTime() - dataA.getTime();
        });
    };

    // ==============================================
    // RESET FORM
    // ==============================================
    const resetForm = () => {
        setFormData({
            nome: '',
            descricao: '',
            codigo: '',
            precoUnitario: '',
            precoVenda: '',
            precoCompra: '',
            compraQuantidade: 0,
            stockAtual: 0,
            stockMinimo: 0,
            unidadeMedida: 'UN',
            categoriaId: '',
            fornecedorId: '',
            ativo: true,
            impostos: {
                iva: null,
                ispc: 0,
                usarIvaEmpresa: true
            }
        });
        setStockFormData({
            quantidade: 1,
            precoUnitario: 0,
            fornecedorId: '',
            notaFiscal: '',
            observacao: ''
        });
        setCreateMessage({ text: '', type: '' });
        setActiveTab('geral');
    };

    const resetEditForm = () => {
        setEditFormDataState(null);
        setEditMessage({ text: '', type: '' });
        setEditActiveTab('geral');
    };

    // ==============================================
    // HANDLE CHANGE - CRIAÇÃO
    // ==============================================
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.startsWith('impostos.')) {
            const field = name.split('.')[1];

            if (field === 'iva') {
                const val = value === '' ? null : value;
                setFormData(prev => ({
                    ...prev,
                    impostos: { ...prev.impostos, [field]: val }
                }));
            } else if (field === 'usarIvaEmpresa') {
                setFormData(prev => ({
                    ...prev,
                    impostos: {
                        ...prev.impostos,
                        [field]: checked,
                        iva: checked ? null : prev.impostos.iva
                    }
                }));
            } else {
                const val = type === 'checkbox' ? checked : value;
                setFormData(prev => ({
                    ...prev,
                    impostos: { ...prev.impostos, [field]: val }
                }));
            }
        } else if (name === 'precoUnitario') {
            const val = sanitizarPreco(value);
            setFormData(prev => ({
                ...prev,
                [name]: val
            }));
        } else if (name === 'precoVenda') {
            const val = sanitizarPreco(value);
            setFormData(prev => ({
                ...prev,
                [name]: val
            }));
        } else {
            const val = type === 'checkbox' ? checked : value;
            setFormData(prev => ({ ...prev, [name]: val }));
        }
    };

    // ==============================================
    // HANDLE CHANGE - EDIÇÃO
    // ==============================================
    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.startsWith('impostos.')) {
            const field = name.split('.')[1];

            if (field === 'iva') {
                const val = value === '' ? null : value;
                setEditFormDataState(prev => ({
                    ...prev,
                    impostos: { ...prev.impostos, [field]: val }
                }));
            } else if (field === 'usarIvaEmpresa') {
                setEditFormDataState(prev => ({
                    ...prev,
                    impostos: {
                        ...prev.impostos,
                        [field]: checked,
                        iva: checked ? null : prev.impostos.iva
                    }
                }));
            } else {
                const val = type === 'checkbox' ? checked : value;
                setEditFormDataState(prev => ({
                    ...prev,
                    impostos: { ...prev.impostos, [field]: val }
                }));
            }
        } else if (name === 'precoUnitario') {
            const val = sanitizarPreco(value);
            setEditFormDataState(prev => ({
                ...prev,
                [name]: val
            }));
        } else if (name === 'precoVenda') {
            const val = sanitizarPreco(value);
            setEditFormDataState(prev => ({
                ...prev,
                [name]: val
            }));
        } else {
            const val = type === 'checkbox' ? checked : value;
            setEditFormDataState(prev => ({ ...prev, [name]: val }));
        }
    };

    // ==============================================
    // 🔥 HANDLE CHANGE - STOCK FORM
    // ==============================================
    const handleStockChange = (e) => {
        const { name, value } = e.target;
        setStockFormData(prev => ({ ...prev, [name]: value }));
    };

    // ==============================================
    // 🔥 SINCRONIZAR PREÇO DE COMPRA COM PREÇO UNITÁRIO DO STOCK
    // ==============================================
    useEffect(() => {
        const precoCompra = parseFloat(formData.precoCompra);
        if (precoCompra > 0) {
            setStockFormData(prev => ({
                ...prev,
                precoUnitario: precoCompra
            }));
        }
    }, [formData.precoCompra]);

    // ==============================================
    // 🔥 SINCRONIZAR QUANTIDADE DO STOCK COM STOCK ATUAL
    // ==============================================
    useEffect(() => {
        const quantidade = parseInt(stockFormData.quantidade);
        if (quantidade > 0) {
            setFormData(prev => ({
                ...prev,
                stockAtual: quantidade,
                compraQuantidade: quantidade
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                stockAtual: 0,
                compraQuantidade: 0
            }));
        }
    }, [stockFormData.quantidade]);

    // ==============================================
    // 🔥 EFECTO PARA CALCULAR PREÇO DE VENDA A PARTIR DO PREÇO UNITÁRIO (CRIAÇÃO)
    // ==============================================
    const isUpdatingPrice = useRef(false);

    useEffect(() => {
        if (isUpdatingPrice.current) return;

        const precoUnitario = parseFloat(formData.precoUnitario) || 0;
        const ivaEfetivo = getIvaEfetivo(formData.impostos);

        if (precoUnitario > 0 && ivaEfetivo !== null && ivaEfetivo !== undefined) {
            const precoComIva = calcularPrecoComIVA(precoUnitario, ivaEfetivo);

            if (precoComIva !== null) {
                isUpdatingPrice.current = true;
                setFormData(prev => ({
                    ...prev,
                    precoVenda: Math.round(precoComIva * 100) / 100
                }));
                setTimeout(() => {
                    isUpdatingPrice.current = false;
                }, 100);
            }
        } else if (precoUnitario > 0 && (ivaEfetivo === null || ivaEfetivo === undefined)) {
            isUpdatingPrice.current = true;
            setFormData(prev => ({
                ...prev,
                precoVenda: precoUnitario
            }));
            setTimeout(() => {
                isUpdatingPrice.current = false;
            }, 100);
        }
    }, [formData.precoUnitario, formData.impostos.iva, formData.impostos.usarIvaEmpresa]);

    // ==============================================
    // 🔥 EFECTO PARA CALCULAR PREÇO DE VENDA A PARTIR DO PREÇO UNITÁRIO (EDIÇÃO)
    // ==============================================
    const isUpdatingEditPrice = useRef(false);

    useEffect(() => {
        if (!editFormDataState) return;
        if (isUpdatingEditPrice.current) return;

        const precoUnitario = parseFloat(editFormDataState.precoUnitario) || 0;
        const ivaEfetivo = getIvaEfetivo(editFormDataState.impostos);

        if (precoUnitario > 0 && ivaEfetivo !== null && ivaEfetivo !== undefined) {
            const precoComIva = calcularPrecoComIVA(precoUnitario, ivaEfetivo);

            if (precoComIva !== null) {
                isUpdatingEditPrice.current = true;
                setEditFormDataState(prev => ({
                    ...prev,
                    precoVenda: Math.round(precoComIva * 100) / 100
                }));
                setTimeout(() => {
                    isUpdatingEditPrice.current = false;
                }, 100);
            }
        } else if (precoUnitario > 0 && (ivaEfetivo === null || ivaEfetivo === undefined)) {
            isUpdatingEditPrice.current = true;
            setEditFormDataState(prev => ({
                ...prev,
                precoVenda: precoUnitario
            }));
            setTimeout(() => {
                isUpdatingEditPrice.current = false;
            }, 100);
        }
    }, [editFormDataState?.precoUnitario, editFormDataState?.impostos?.iva, editFormDataState?.impostos?.usarIvaEmpresa]);

    // ==============================================
    // 🔥 DADOS PARA GRÁFICOS (compatíveis com Recharts)
    // ==============================================
    const dadosGraficos = useMemo(() => {
        const categoriasCount = {};
        const statusCount = { 'Sem Stock': 0, 'Stock Baixo': 0, 'Stock Médio': 0, 'Stock Alto': 0 };
        const precosPorCategoria = {};
        const stockPorCategoria = {};
        let valorTotalStock = 0;

        produtos.forEach(p => {
            const catNome = p.categoriaId?.nome || 'Sem Categoria';
            categoriasCount[catNome] = (categoriasCount[catNome] || 0) + 1;

            const status = getStatusStock(p.stockAtual, p.stockMinimo);
            statusCount[status.label] = (statusCount[status.label] || 0) + 1;

            if (!precosPorCategoria[catNome]) {
                precosPorCategoria[catNome] = { total: 0, count: 0 };
            }
            precosPorCategoria[catNome].total += p.precoVenda || 0;
            precosPorCategoria[catNome].count += 1;

            stockPorCategoria[catNome] = (stockPorCategoria[catNome] || 0) + (p.stockAtual || 0);
            valorTotalStock += (p.stockAtual || 0) * (p.precoVenda || 0);
        });

        // 🔥 Recharts espera arrays de objetos (um objeto por ponto/barra)
        const categoriasData = Object.keys(categoriasCount).map(label => ({
            label,
            quantidade: categoriasCount[label]
        }));

        const statusData = Object.keys(statusCount).map(label => ({
            name: label,
            value: statusCount[label]
        }));

        const precosData = Object.keys(precosPorCategoria).map(label => ({
            label,
            preco: precosPorCategoria[label].total / precosPorCategoria[label].count
        }));

        const stockData = Object.keys(stockPorCategoria).map(label => ({
            label,
            stock: stockPorCategoria[label]
        }));

        return {
            categorias: categoriasData,
            status: statusData,
            precos: precosData,
            stock: stockData,
            valorTotalStock,
            totalProdutos: produtos.length,
            totalCategorias: Object.keys(categoriasCount).length
        };
    }, [produtos]);

    // ==============================================
    // BUSCAR DADOS
    // ==============================================
    const fetchLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    setLoggedUser(storedUser);
                    const empresaIdExtraido = obterEmpresaIdDoUtilizador(storedUser);
                    setEmpresaId(empresaIdExtraido);
                    return storedUser;
                }
                return null;
            }

            const response = await axios.get(`${API_BASE_URL}/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                const userData = response.data.data;
                setLoggedUser(userData);
                const empresaIdExtraido = obterEmpresaIdDoUtilizador(userData);
                setEmpresaId(empresaIdExtraido);
                localStorage.setItem('usuario', JSON.stringify(userData));
                return userData;
            }
        } catch (err) {
            console.error('❌ Erro ao buscar utilizador:', err);
        }
        return null;
    };

    const fetchProdutos = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Sessão expirada. Faça login novamente.');
                setLoading(false);
                return;
            }

            const response = await api.get('/produtos');

            let produtosData = [];
            let config = { ...CONFIG_PADRAO };

            if (response.data) {
                if (response.data.success && response.data.data) {
                    produtosData = response.data.data;
                    if (response.data.configuracao) {
                        config = {
                            ...CONFIG_PADRAO,
                            ...response.data.configuracao,
                            moeda: {
                                ...CONFIG_PADRAO.moeda,
                                ...(response.data.configuracao.moeda || {})
                            }
                        };
                    }
                } else if (Array.isArray(response.data)) {
                    produtosData = response.data;
                }
            }

            setConfigEmpresa(config);
            setProdutos(produtosData);
            filtrarProdutos(produtosData, searchTerm, filtros);

        } catch (err) {
            console.error('❌ Erro ao carregar produtos:', err);
            setError(err.response?.data?.erro || err.message || 'Erro ao carregar produtos');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategorias = async () => {
        try {
            const response = await api.get('/categorias');
            if (response.data) {
                const categoriasData = Array.isArray(response.data) ? response.data : response.data.data || [];
                setCategorias(categoriasData);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar categorias:', err);
        }
    };

    const fetchFornecedores = async () => {
        try {
            const response = await api.get('/fornecedores');
            if (response.data) {
                const fornecedoresData = Array.isArray(response.data) ? response.data : response.data.data || [];
                setFornecedores(fornecedoresData);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar fornecedores:', err);
        }
    };

    // ==============================================
    // FILTRAR PRODUTOS (COM ORDENAÇÃO POR DATA)
    // ==============================================
    const filtrarProdutos = useCallback((produtosList = produtos, term = searchTerm, filtrosList = filtros) => {
        let resultados = [...produtosList];

        if (term && term.trim() !== '') {
            const searchLower = term.toLowerCase().trim();
            resultados = resultados.filter(p => {
                const nome = p.nome || '';
                const codigo = p.codigo || '';
                const descricao = p.descricao || '';
                const categoria = p.categoriaId?.nome || '';
                const fornecedor = p.fornecedorId?.nome || '';

                return nome.toLowerCase().includes(searchLower) ||
                    codigo.toLowerCase().includes(searchLower) ||
                    descricao.toLowerCase().includes(searchLower) ||
                    categoria.toLowerCase().includes(searchLower) ||
                    fornecedor.toLowerCase().includes(searchLower);
            });
        }

        if (filtrosList.categoria && filtrosList.categoria !== 'todos') {
            resultados = resultados.filter(p =>
                p.categoriaId?._id === filtrosList.categoria ||
                p.categoriaId === filtrosList.categoria
            );
        }

        if (filtrosList.fornecedor && filtrosList.fornecedor !== 'todos') {
            resultados = resultados.filter(p =>
                p.fornecedorId?._id === filtrosList.fornecedor ||
                p.fornecedorId === filtrosList.fornecedor
            );
        }

        if (filtrosList.statusStock && filtrosList.statusStock !== 'todos') {
            if (filtrosList.statusStock === 'sem_stock') {
                resultados = resultados.filter(p => p.stockAtual === 0);
            } else if (filtrosList.statusStock === 'baixo_stock') {
                resultados = resultados.filter(p => p.stockAtual > 0 && p.stockAtual <= (p.stockMinimo || 0));
            } else if (filtrosList.statusStock === 'stock_medio') {
                resultados = resultados.filter(p => p.stockAtual > (p.stockMinimo || 0) && p.stockAtual < 10);
            } else if (filtrosList.statusStock === 'stock_alto') {
                resultados = resultados.filter(p => p.stockAtual >= 10);
            }
        }

        if (filtrosList.unidadeMedida && filtrosList.unidadeMedida !== 'todos') {
            resultados = resultados.filter(p => p.unidadeMedida === filtrosList.unidadeMedida);
        }

        if (filtrosList.ativo && filtrosList.ativo !== 'todos') {
            const isAtivo = filtrosList.ativo === 'ativo';
            resultados = resultados.filter(p => p.ativo === isAtivo);
        }

        resultados = ordenarProdutos(resultados);

        setFilteredProdutos(resultados);

        const total = resultados.length;
        const pages = Math.ceil(total / pagination.limit) || 1;
        setPagination(prev => ({
            ...prev,
            total: total,
            pages: pages,
            page: prev.page > pages ? 1 : prev.page
        }));
    }, [produtos, searchTerm, filtros, pagination.limit]);

    // ==============================================
    // 🔥 CRIAÇÃO DE PRODUTO
    // ==============================================
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        setCreateMessage({ text: '', type: '' });

        if (!formData.nome.trim()) {
            setCreateMessage({ text: 'O nome do produto é obrigatório', type: 'danger' });
            setCreateLoading(false);
            return;
        }

        if (!formData.precoVenda || parseFloat(formData.precoVenda) <= 0) {
            setCreateMessage({ text: 'Preço de venda inválido', type: 'danger' });
            setCreateLoading(false);
            return;
        }

        let empresaParaEnviar = empresaId;
        if (!empresaParaEnviar && loggedUser) {
            empresaParaEnviar = obterEmpresaIdDoUtilizador(loggedUser);
        }

        if (!empresaParaEnviar) {
            setCreateMessage({
                text: 'Utilizador sem empresa associada. Contacte o administrador.',
                type: 'danger'
            });
            setCreateLoading(false);
            return;
        }

        const stockQuantidade = parseInt(stockFormData.quantidade) || 0;
        const stockPreco = parseFloat(stockFormData.precoUnitario) || 0;
        const quantidadeInicial = parseInt(formData.compraQuantidade) || 0;
        const precoCompraInicial = parseFloat(formData.precoCompra) || 0;

        let quantidadeFinal = 0;
        let precoFinal = 0;
        let historicoEntradas = [];

        if (stockQuantidade > 0 && stockPreco > 0) {
            quantidadeFinal = stockQuantidade;
            precoFinal = stockPreco;
            historicoEntradas = [{
                quantidade: stockQuantidade,
                precoUnitario: stockPreco,
                fornecedorId: stockFormData.fornecedorId || null,
                dataEntrada: new Date().toISOString(),
                observacao: stockFormData.observacao || 'Entrada inicial na criação do produto',
                criadoPor: loggedUser?._id || loggedUser?.id || null,
                notaFiscal: stockFormData.notaFiscal || null
            }];
        } else if (quantidadeInicial > 0) {
            quantidadeFinal = quantidadeInicial;
            precoFinal = precoCompraInicial;
            historicoEntradas = [{
                quantidade: quantidadeInicial,
                precoUnitario: precoCompraInicial,
                fornecedorId: formData.fornecedorId || null,
                dataEntrada: new Date().toISOString(),
                observacao: 'Entrada inicial na criação do produto',
                criadoPor: loggedUser?._id || loggedUser?.id || null
            }];
        }

        let impostosEnviar = {
            iva: null,
            ispc: parseFloat(formData.impostos.ispc) || 0,
            usarIvaEmpresa: formData.impostos.usarIvaEmpresa
        };

        if (!formData.impostos.usarIvaEmpresa) {
            const ivaValue = formData.impostos.iva !== '' && formData.impostos.iva !== null
                ? parseFloat(formData.impostos.iva)
                : null;
            impostosEnviar.iva = ivaValue;
        } else {
            impostosEnviar.iva = null;
        }

        const dadosParaEnviar = {
            nome: formData.nome.trim(),
            descricao: formData.descricao?.trim() || '',
            codigo: formData.codigo?.trim() || '',
            precoVenda: parseFloat(formData.precoVenda) || 0,
            precoCompra: precoFinal,
            compraQuantidade: quantidadeFinal,
            stockAtual: quantidadeFinal,
            stockMinimo: parseInt(formData.stockMinimo) || 0,
            unidadeMedida: formData.unidadeMedida || 'UN',
            categoriaId: formData.categoriaId || null,
            fornecedorId: stockFormData.fornecedorId || formData.fornecedorId || null,
            empresaId: empresaParaEnviar,
            ativo: formData.ativo !== undefined ? formData.ativo : true,
            impostos: impostosEnviar,
            historicoEntradas: historicoEntradas
        };

        try {
            const response = await api.post('/produtos', dadosParaEnviar);
            if (response.data) {
                setCreateMessage({ text: 'Produto criado com sucesso!', type: 'success' });
                setTimeout(() => {
                    setShowCreateModal(false);
                    resetForm();
                    fetchProdutos();
                }, 1500);
            }
        } catch (err) {
            console.error('❌ Erro ao criar produto:', err);
            setCreateMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao criar produto',
                type: 'danger'
            });
        } finally {
            setCreateLoading(false);
        }
    };

    // ==============================================
    // EDIÇÃO DE PRODUTO
    // ==============================================
    const handleEdit = async (produto) => {
        try {
            const response = await api.get(`/produtos/${produto._id}`);
            const produtoCompleto = response.data.data || produto;

            const ivaOriginal = produtoCompleto.impostos?.iva !== undefined ? produtoCompleto.impostos.iva : null;

            const ivaEfetivo = getIvaEfetivo(produtoCompleto.impostos);
            const precoVenda = parseFloat(produtoCompleto.precoVenda) || 0;
            let precoUnitario = precoVenda;

            if (ivaEfetivo !== null && ivaEfetivo !== undefined && precoVenda > 0) {
                const precoSemIva = calcularPrecoSemIVA(precoVenda, ivaEfetivo);
                if (precoSemIva !== null) {
                    precoUnitario = precoSemIva;
                }
            }

            setEditFormDataState({
                _id: produtoCompleto._id,
                nome: produtoCompleto.nome || '',
                descricao: produtoCompleto.descricao || '',
                codigo: produtoCompleto.codigo || '',
                precoUnitario: precoUnitario > 0 ? Math.round(precoUnitario * 100) / 100 : '',
                precoVenda: produtoCompleto.precoVenda || '',
                precoCompra: produtoCompleto.precoCompra || '',
                compraQuantidade: produtoCompleto.compraQuantidade || 0,
                stockAtual: produtoCompleto.stockAtual || 0,
                stockMinimo: produtoCompleto.stockMinimo || 0,
                unidadeMedida: produtoCompleto.unidadeMedida || 'UN',
                categoriaId: produtoCompleto.categoriaId?._id || produtoCompleto.categoriaId || '',
                fornecedorId: produtoCompleto.fornecedorId?._id || produtoCompleto.fornecedorId || '',
                ativo: produtoCompleto.ativo !== undefined ? produtoCompleto.ativo : true,
                impostos: {
                    iva: ivaOriginal,
                    ispc: produtoCompleto.impostos?.ispc || 0,
                    usarIvaEmpresa: produtoCompleto.impostos?.usarIvaEmpresa !== undefined ?
                        produtoCompleto.impostos.usarIvaEmpresa : true
                }
            });

            setEditActiveTab('geral');
            setShowEditModal(true);

        } catch (error) {
            console.error('Erro ao carregar produto:', error);
            showTempToast('Erro', 'Erro ao carregar dados do produto', 'danger');
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        setEditMessage({ text: '', type: '' });

        if (!editFormDataState.nome.trim()) {
            setEditMessage({ text: 'O nome do produto é obrigatório', type: 'danger' });
            setEditLoading(false);
            return;
        }

        if (!editFormDataState.precoVenda || parseFloat(editFormDataState.precoVenda) <= 0) {
            setEditMessage({ text: 'Preço de venda inválido', type: 'danger' });
            setEditLoading(false);
            return;
        }

        try {
            const { _id, ...updateData } = editFormDataState;
            delete updateData.stockAtual;

            if (updateData.impostos) {
                if (updateData.impostos.usarIvaEmpresa === true) {
                    updateData.impostos.iva = null;
                }
            }

            const response = await api.put(`/produtos/${_id}`, updateData);

            if (response.data) {
                setEditMessage({ text: 'Produto atualizado com sucesso!', type: 'success' });
                setTimeout(() => {
                    setShowEditModal(false);
                    resetEditForm();
                    fetchProdutos();
                }, 1500);
            }
        } catch (err) {
            console.error('❌ Erro ao atualizar produto:', err);
            setEditMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao atualizar produto',
                type: 'danger'
            });
        } finally {
            setEditLoading(false);
        }
    };

    // ==============================================
    // HANDLERS - FILTROS
    // ==============================================
    const aplicarFiltros = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        filtrarProdutos(produtos, searchTerm, filtros);
    };

    const limparFiltros = () => {
        setFiltros({
            categoria: 'todos',
            fornecedor: 'todos',
            statusStock: 'todos',
            unidadeMedida: 'todos',
            ativo: 'todos'
        });
        setSearchTerm('');
        setShowFiltros(false);
        setTimeout(() => {
            setPagination(prev => ({ ...prev, page: 1 }));
            filtrarProdutos(produtos, '', {
                categoria: 'todos',
                fornecedor: 'todos',
                statusStock: 'todos',
                unidadeMedida: 'todos',
                ativo: 'todos'
            });
        }, 50);
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination(prev => ({ ...prev, page }));
        const tableContainer = document.querySelector('.listar-produtos-table-container');
        if (tableContainer) {
            tableContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // ==============================================
    // EXPORTAÇÃO
    // ==============================================
    const exportarProdutos = async (formato) => {
        setExportLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('Sessão expirada. Faça login novamente.');
                return;
            }

            const params = new URLSearchParams();
            params.append('formato', formato);
            if (searchTerm) params.append('search', searchTerm);
            if (filtros.categoria && filtros.categoria !== 'todos') params.append('categoria', filtros.categoria);
            if (filtros.fornecedor && filtros.fornecedor !== 'todos') params.append('fornecedor', filtros.fornecedor);
            if (filtros.statusStock && filtros.statusStock !== 'todos') params.append('statusStock', filtros.statusStock);
            if (filtros.unidadeMedida && filtros.unidadeMedida !== 'todos') params.append('unidadeMedida', filtros.unidadeMedida);
            if (filtros.ativo && filtros.ativo !== 'todos') params.append('ativo', filtros.ativo);

            const url = `${API_BASE_URL}/produtos/exportar?${params.toString()}`;

            const response = await axios({
                method: 'GET',
                url: url,
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const contentType = response.headers['content-type'] || '';
            if (contentType.includes('application/json')) {
                const text = await response.data.text();
                try {
                    const error = JSON.parse(text);
                    throw new Error(error.erro || 'Erro na exportação');
                } catch {
                    throw new Error('Erro na exportação');
                }
            }

            const extensao = formato === 'excel' || formato === 'xlsx' ? 'xlsx' : 'csv';
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] ||
                    (formato === 'csv' ? 'text/csv;charset=utf-8' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
            });

            const urlDownload = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlDownload;
            link.download = `produtos_${new Date().toISOString().split('T')[0]}.${extensao}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlDownload);

            showTempToast('Sucesso', `Produtos exportados em ${formato.toUpperCase()}`, 'success');
        } catch (err) {
            console.error('❌ Erro na exportação:', err);
            showTempToast('Erro', err.message || 'Falha na exportação', 'danger');
        } finally {
            setExportLoading(false);
        }
    };

    // ==============================================
    // EFECTOS
    // ==============================================
    useEffect(() => {
        const carregarDados = async () => {
            await fetchLoggedUser();
            await Promise.all([
                fetchProdutos(),
                fetchCategorias(),
                fetchFornecedores()
            ]);
        };
        carregarDados();
    }, []);

    useEffect(() => {
        if (produtos.length > 0) {
            filtrarProdutos(produtos, searchTerm, filtros);
        }
    }, [searchTerm, filtros, produtos, filtrarProdutos]);

    // ==============================================
    // PAGINAÇÃO FRONTEND
    // ==============================================
    const currentProdutos = useMemo(() => {
        const startIndex = (pagination.page - 1) * pagination.limit;
        const endIndex = startIndex + pagination.limit;
        return filteredProdutos.slice(startIndex, endIndex);
    }, [filteredProdutos, pagination.page, pagination.limit]);

    // ==============================================
    // RENDER PAGINAÇÃO
    // ==============================================
    const renderPagination = () => {
        const { page, pages, total, limit } = pagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * limit + 1;
        const endItem = Math.min(page * limit, total);

        return (
            <div className="listar-produtos-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} produtos</span>
                </div>
                <div className="pagination-controls">
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                    >
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {Array.from({ length: Math.min(pages, 10) }, (_, i) => {
                            let pageNum;
                            if (pages <= 10) {
                                pageNum = i + 1;
                            } else if (page <= 6) {
                                pageNum = i + 1;
                            } else if (page >= pages - 5) {
                                pageNum = pages - 9 + i;
                            } else {
                                pageNum = page - 5 + i;
                            }
                            if (pageNum > 0 && pageNum <= pages) {
                                return (
                                    <button
                                        key={pageNum}
                                        className={`pagination-number ${page === pageNum ? 'active' : ''}`}
                                        onClick={() => handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            }
                            return null;
                        })}
                        {pages > 10 && page < pages - 5 && (
                            <>
                                <span className="pagination-dots">...</span>
                                <button
                                    className="pagination-number"
                                    onClick={() => handlePageChange(pages)}
                                >
                                    {pages}
                                </button>
                            </>
                        )}
                    </div>
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === pages}
                    >
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // RENDER PRINCIPAL
    // ==============================================
    const precoUnitarioAtual = parseFloat(formData.precoUnitario) || 0;
    const ivaEfetivo = getIvaEfetivo(formData.impostos);
    const precoVendaCalculado = calcularPrecoComIVA(precoUnitarioAtual, ivaEfetivo);

    const precoUnitarioEdit = editFormDataState ? parseFloat(editFormDataState.precoUnitario) || 0 : 0;
    const ivaEfetivoEdit = editFormDataState ? getIvaEfetivo(editFormDataState.impostos) : null;
    const precoVendaCalculadoEdit = calcularPrecoComIVA(precoUnitarioEdit, ivaEfetivoEdit);

    return (
        <div className="listar-produtos-container">
           {/* ==========================================
    MODAL DE CRIAÇÃO
    ========================================== */}
{showCreateModal && (
    <div className="listar-produto-modal-overlay" onClick={() => { setShowCreateModal(false); resetForm(); }}>
        <div className="listar-produto-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="listar-produto-modal-header">
                <h3><Icon name="add_box" size={22} /> Novo Produto</h3>
                <button className="listar-produto-modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
            </div>
            <div className="listar-produto-modal-body">
                {createMessage.text && (
                    <div className={`listar-produto-modal-alert listar-produto-modal-alert-${createMessage.type}`}>
                        <Icon name={createMessage.type === 'success' ? 'check_circle' : 'error'} size={18} />
                        <span>{createMessage.text}</span>
                        <button className="listar-produto-alert-close" onClick={() => setCreateMessage({ text: '', type: '' })}>×</button>
                    </div>
                )}
                <form onSubmit={handleCreateSubmit}>
                    <div className="listar-produto-modal-tabs">
                        <button type="button" className={`listar-produto-modal-tab-btn ${activeTab === 'geral' ? 'active' : ''}`} onClick={() => setActiveTab('geral')}>
                            <Icon name="info" size={16} />
                            <span className="listar-produto-tab-label">Dados Gerais</span>
                        </button>
                        <button type="button" className={`listar-produto-modal-tab-btn ${activeTab === 'impostos' ? 'active' : ''}`} onClick={() => setActiveTab('impostos')}>
                            <Icon name="receipt" size={16} />
                            <span className="listar-produto-tab-label">Impostos</span>
                        </button>
                        <button type="button" className={`listar-produto-modal-tab-btn ${activeTab === 'stock' ? 'active' : ''}`} onClick={() => setActiveTab('stock')}>
                            <Icon name="inventory_2" size={16} />
                            <span className="listar-produto-tab-label">Entrada de Stock</span>
                        </button>
                    </div>

                    {/* TAB DADOS GERAIS */}
                    <div className={`listar-produto-modal-tab-panel ${activeTab === 'geral' ? 'active' : ''}`}>
                        <div className="listar-produto-modal-section">
                            <div className="listar-produto-form-group">
                                <label>Nome <span className="listar-produto-required">*</span></label>
                                <input type="text" name="nome" value={formData.nome} onChange={handleChange} required placeholder="Nome do produto" />
                            </div>
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Código</label>
                                    <input type="text" name="codigo" value={formData.codigo} onChange={handleChange} placeholder="Código (opcional)" />
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>Categoria</label>
                                    <select name="categoriaId" value={formData.categoriaId} onChange={handleChange}>
                                        <option value="">Selecione uma categoria</option>
                                        {categorias.map(cat => (
                                            <option key={cat._id} value={cat._id}>{cat.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Preço Unitário <span className="listar-produto-required">*</span></label>
                                    <input
                                        type="text"
                                        name="precoUnitario"
                                        value={formData.precoUnitario}
                                        onChange={handleChange}
                                        required
                                        placeholder="0.00"
                                        inputMode="decimal"
                                        autoComplete="off"
                                    />
                                    <small className="listar-produto-form-help">Preço base sem IVA</small>
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>Preço de Venda (com IVA)</label>
                                    <input
                                        type="text"
                                        name="precoVenda"
                                        value={formData.precoVenda}
                                        onChange={handleChange}
                                        placeholder="0.00"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        readOnly
                                        
                                    />
                                    {ivaEfetivo !== null && ivaEfetivo !== undefined && precoUnitarioAtual > 0 && (
                                        <small className="listar-produto-form-help listar-produto-help-info">
                                            <Icon name="info" size={12} style={{ marginRight: '0.3rem' }} />
                                            Calculado com IVA de {ivaEfetivo}% (base: {formatarMoeda(precoUnitarioAtual)})
                                        </small>
                                    )}
                                    {ivaEfetivo === null && precoUnitarioAtual > 0 && (
                                        <small className="listar-produto-form-help listar-produto-help-warning">
                                            <Icon name="warning" size={12} style={{ marginRight: '0.3rem' }} />
                                            Sem IVA configurado
                                        </small>
                                    )}
                                </div>
                            </div>
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Preço de Compra</label>
                                    <input type="number" name="precoCompra" value={formData.precoCompra} onChange={handleChange} placeholder="0.00" step="0.01" min="0" />
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>Stock Mínimo</label>
                                    <input type="number" name="stockMinimo" value={formData.stockMinimo} onChange={handleChange} placeholder="0" min="0" />
                                </div>
                            </div>
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Unidade de Medida</label>
                                    <select name="unidadeMedida" value={formData.unidadeMedida} onChange={handleChange}>
                                        {UNIDADES_MEDIDA.map(u => (
                                            <option key={u.value} value={u.value}>{u.label}</option>
                                        ))}
                                    </select>
                                </div>
                                
                            </div>
                            <div className="listar-produto-form-group">
                                <label>Descrição</label>
                                <textarea name="descricao" value={formData.descricao} onChange={handleChange} placeholder="Descrição do produto" rows="2" />
                            </div>
                            <div className="listar-produto-form-group-check-gerais">
                                <label className="listar-produto-checkbox-label">
                                    <input type="checkbox" name="ativo" checked={formData.ativo} onChange={handleChange} />
                                    Produto Ativo
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* TAB IMPOSTOS */}
                    <div className={`listar-produto-modal-tab-panel ${activeTab === 'impostos' ? 'active' : ''}`}>
                        <div className="listar-produto-modal-section">
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>IVA (%)</label>
                                    <input type="number" name="impostos.iva" value={formData.impostos.iva || ''} onChange={handleChange} placeholder="Deixar em branco para usar o da empresa" step="0.01" min="0" max="100" disabled={formData.impostos.usarIvaEmpresa} />
                                    <small className="listar-produto-form-help">{formData.impostos.usarIvaEmpresa ? 'Usando IVA da empresa' : 'IVA específico do produto'}</small>
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>ISPC (%)</label>
                                    <input type="number" name="impostos.ispc" value={formData.impostos.ispc || 0} onChange={handleChange} placeholder="0" step="0.01" min="0" max="100" />
                                </div>
                            </div>
                            <div className="listar-produto-form-group-check-imposto-lista">
                                <label className="listar-produto-checkbox-label">
                                    <input type="checkbox" name="impostos.usarIvaEmpresa" checked={formData.impostos.usarIvaEmpresa} onChange={handleChange} />
                                    Usar IVA da empresa ({configEmpresa?.iva !== null && configEmpresa?.iva !== undefined ? `${configEmpresa.iva}%` : 'N/D'})
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* TAB ENTRADA DE STOCK */}
                    <div className={`listar-produto-modal-tab-panel ${activeTab === 'stock' ? 'active' : ''}`}>
                        <div className="listar-produto-modal-section">
                            <div className="listar-produto-stock-info-box">
                                <p>
                                    <Icon name="info" size={14} style={{ marginRight: '0.6rem' }} />
                                    Preencha os campos abaixo para registrar a entrada inicial de stock deste produto.
                                    A data da entrada será registada automaticamente no momento da criação.
                                </p>
                            </div>

                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Quantidade <span className="listar-produto-required">*</span></label>
                                    <input
                                        type="number"
                                        name="quantidade"
                                        value={stockFormData.quantidade}
                                        onChange={handleStockChange}
                                        min="1"
                                        step="1"
                                        placeholder="1"
                                    />
                                    <small className="listar-produto-form-help">Quantidade a adicionar ao stock</small>
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>Preço Unitário <span className="listar-produto-required">*</span></label>
                                    <input
                                        type="number"
                                        name="precoUnitario"
                                        value={stockFormData.precoUnitario}
                                        onChange={handleStockChange}
                                        min="0.01"
                                        step="0.01"
                                        placeholder="0.00"
                                    />
                                    <small className="listar-produto-form-help">Preço de compra por unidade</small>
                                </div>
                            </div>

                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Fornecedor</label>
                                    <select
                                        name="fornecedorId"
                                        value={stockFormData.fornecedorId}
                                        onChange={handleStockChange}
                                    >
                                        <option value="">Selecione um fornecedor</option>
                                        {fornecedores.map(f => (
                                            <option key={f._id} value={f._id}>{f.nome}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>Nº Nota Fiscal</label>
                                    <input
                                        type="text"
                                        name="notaFiscal"
                                        value={stockFormData.notaFiscal}
                                        onChange={handleStockChange}
                                        placeholder="Número da nota fiscal (opcional)"
                                    />
                                </div>
                            </div>

                            <div className="listar-produto-form-group-observacao">
                                <label>Observação</label>
                                <textarea
                                    name="observacao"
                                    value={stockFormData.observacao}
                                    onChange={handleStockChange}
                                    placeholder="Observações adicionais (opcional)"
                                    rows="2"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="listar-produto-modal-actions">
                        <button type="submit" className="listar-produto-modal-btn-primary" disabled={createLoading}>
                            {createLoading ? 'A criar...' : 'Criar Produto'}
                        </button>
                        <button type="button" className="listar-produto-modal-btn-secondary" onClick={resetForm}>Limpar</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
)}

{/* ==========================================
    MODAL DE EDIÇÃO
    ========================================== */}
{showEditModal && editFormDataState && (
    <div className="listar-produto-modal-overlay" onClick={() => { setShowEditModal(false); resetEditForm(); }}>
        <div className="listar-produto-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="listar-produto-modal-header">
                <h3><Icon name="edit" size={22} /> Editar Produto</h3>
                <button className="listar-produto-modal-close" onClick={() => { setShowEditModal(false); resetEditForm(); }}>×</button>
            </div>
            <div className="listar-produto-modal-body">
                {editMessage.text && (
                    <div className={`listar-produto-modal-alert listar-produto-modal-alert-${editMessage.type}`}>
                        <Icon name={editMessage.type === 'success' ? 'check_circle' : 'error'} size={18} />
                        <span>{editMessage.text}</span>
                        <button className="listar-produto-alert-close" onClick={() => setEditMessage({ text: '', type: '' })}>×</button>
                    </div>
                )}
                <form onSubmit={handleEditSubmit}>
                    <div className="listar-produto-modal-tabs">
                        <button type="button" className={`listar-produto-modal-tab-btn ${editActiveTab === 'geral' ? 'active' : ''}`} onClick={() => setEditActiveTab('geral')}>
                            <Icon name="info" size={16} />
                            <span className="listar-produto-tab-label">Dados Gerais</span>
                        </button>
                        <button type="button" className={`listar-produto-modal-tab-btn ${editActiveTab === 'impostos' ? 'active' : ''}`} onClick={() => setEditActiveTab('impostos')}>
                            <Icon name="receipt" size={16} />
                            <span className="listar-produto-tab-label">Impostos</span>
                        </button>
                    </div>

                    <div className={`listar-produto-modal-tab-panel ${editActiveTab === 'geral' ? 'active' : ''}`}>
                        <div className="listar-produto-modal-section">
                            <div className="listar-produto-form-group">
                                <label>Nome <span className="listar-produto-required">*</span></label>
                                <input type="text" name="nome" value={editFormDataState.nome} onChange={handleEditChange} required placeholder="Nome do produto" />
                            </div>
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Código</label>
                                    <input type="text" name="codigo" value={editFormDataState.codigo} onChange={handleEditChange} placeholder="Código" disabled />
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>Categoria</label>
                                    <select name="categoriaId" value={editFormDataState.categoriaId} onChange={handleEditChange}>
                                        <option value="">Selecione uma categoria</option>
                                        {categorias.map(cat => (
                                            <option key={cat._id} value={cat._id}>{cat.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Preço Unitário <span className="listar-produto-required">*</span></label>
                                    <input
                                        type="text"
                                        name="precoUnitario"
                                        value={editFormDataState.precoUnitario}
                                        onChange={handleEditChange}
                                        required
                                        placeholder="0.00"
                                        inputMode="decimal"
                                        autoComplete="off"
                                    />
                                    <small className="listar-produto-form-help">Preço base sem IVA</small>
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>Preço de Venda (com IVA)</label>
                                    <input
                                        type="text"
                                        name="precoVenda"
                                        value={editFormDataState.precoVenda}
                                        onChange={handleEditChange}
                                        placeholder="0.00"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        readOnly
                                        
                                    />
                                    {ivaEfetivoEdit !== null && ivaEfetivoEdit !== undefined && precoUnitarioEdit > 0 && (
                                        <small className="listar-produto-form-help listar-produto-help-info">
                                            <Icon name="info" size={12} style={{ marginRight: '0.3rem' }} />
                                            Calculado com IVA de {ivaEfetivoEdit}% (base: {formatarMoeda(precoUnitarioEdit)})
                                        </small>
                                    )}
                                    {ivaEfetivoEdit === null && precoUnitarioEdit > 0 && (
                                        <small className="listar-produto-form-help listar-produto-help-warning">
                                            <Icon name="warning" size={12} style={{ marginRight: '0.3rem' }} />
                                            Sem IVA configurado
                                        </small>
                                    )}
                                </div>
                            </div>
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Preço de Compra</label>
                                    <input type="number" name="precoCompra" value={editFormDataState.precoCompra} onChange={handleEditChange} placeholder="0.00" step="0.01" min="0" />
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>Stock Atual</label>
                                    <input
                                        type="number"
                                        name="stockAtual"
                                        value={editFormDataState.stockAtual}
                                        onChange={handleEditChange}
                                        placeholder="0"
                                        min="0"
                                        disabled
                                        className="listar-produto-field-disabled"
                                    />
                                    <small className="listar-produto-form-help">⚠️ O stock não pode ser alterado na edição. Utilize o módulo de entradas de stock.</small>
                                </div>
                            </div>
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Stock Mínimo</label>
                                    <input type="number" name="stockMinimo" value={editFormDataState.stockMinimo} onChange={handleEditChange} placeholder="0" min="0" />
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>Unidade de Medida</label>
                                    <select name="unidadeMedida" value={editFormDataState.unidadeMedida} onChange={handleEditChange}>
                                        {UNIDADES_MEDIDA.map(u => (
                                            <option key={u.value} value={u.value}>{u.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>Fornecedor</label>
                                    <select name="fornecedorId" value={editFormDataState.fornecedorId} onChange={handleEditChange}>
                                        <option value="">Selecione um fornecedor</option>
                                        {fornecedores.map(f => (
                                            <option key={f._id} value={f._id}>{f.nome}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="listar-produto-form-group">
                                    {/* Espaço vazio */}
                                </div>
                            </div>
                            <div className="listar-produto-form-group">
                                <label>Descrição</label>
                                <textarea name="descricao" value={editFormDataState.descricao} onChange={handleEditChange} placeholder="Descrição do produto" rows="2" />
                            </div>
                            <div className="listar-produto-form-group-check-lista-editar-produto">
                                <label className="listar-produto-checkbox-label">
                                    <input type="checkbox" name="ativo" checked={editFormDataState.ativo} onChange={handleEditChange} />
                                    Produto Ativo
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className={`listar-produto-modal-tab-panel ${editActiveTab === 'impostos' ? 'active' : ''}`}>
                        <div className="listar-produto-modal-section">
                            <div className="listar-produto-form-row">
                                <div className="listar-produto-form-group">
                                    <label>IVA (%)</label>
                                    <input type="number" name="impostos.iva" value={editFormDataState.impostos.iva || ''} onChange={handleEditChange} placeholder="Deixar em branco para usar o da empresa" step="0.01" min="0" max="100" disabled={editFormDataState.impostos.usarIvaEmpresa} />
                                    <small className="listar-produto-form-help">{editFormDataState.impostos.usarIvaEmpresa ? 'Usando IVA da empresa' : 'IVA específico do produto'}</small>
                                </div>
                                <div className="listar-produto-form-group">
                                    <label>ISPC (%)</label>
                                    <input type="number" name="impostos.ispc" value={editFormDataState.impostos.ispc || 0} onChange={handleEditChange} placeholder="0" step="0.01" min="0" max="100" />
                                </div>
                            </div>
                            <div className="listar-produto-form-group-check-lista-imposto-produto">
                                <label className="listar-produto-checkbox-label">
                                    <input type="checkbox" name="impostos.usarIvaEmpresa" checked={editFormDataState.impostos.usarIvaEmpresa} onChange={handleEditChange} />
                                    Usar IVA da empresa ({configEmpresa?.iva !== null && configEmpresa?.iva !== undefined ? `${configEmpresa.iva}%` : 'N/D'})
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="listar-produto-modal-actions">
                        <button type="submit" className="listar-produto-modal-btn-primary" disabled={editLoading}>
                            {editLoading ? 'A atualizar...' : 'Salvar Alterações'}
                        </button>
                        <button type="button" className="listar-produto-modal-btn-secondary" onClick={() => { setShowEditModal(false); resetEditForm(); }}>Cancelar</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
)}

            {/* HEADER */}
            <div className="listar-produtos-header">
                <div className="header-title">
                    <h2><Icon name="inventory_2" size={24} /> Listar Produtos</h2>
                    <p>Visualize e gerencie todos os produtos da sua empresa</p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-add"
                        onClick={() => setShowCreateModal(true)}
                        title="Adicionar Produto"
                    >
                        <Icon name="add" size={18} />
                        Adicionar
                    </button>
                    <button
                        className="btn-export"
                        onClick={() => exportarProdutos('excel')}
                        disabled={exportLoading}
                        title="Exportar Excel"
                    >
                        <Icon name="file_download" size={18} />
                        {exportLoading ? 'A exportar...' : 'Excel'}
                    </button>
                    <button
                        className="btn-export"
                        onClick={() => exportarProdutos('csv')}
                        disabled={exportLoading}
                        title="Exportar CSV"
                    >
                        <Icon name="file_download" size={18} />
                        {exportLoading ? 'A exportar...' : 'CSV'}
                    </button>
                    <button
                        className="btn-refresh"
                        onClick={fetchProdutos}
                        title="Atualizar"
                    >
                        <Icon name="refresh" size={18} />
                    </button>
                </div>
            </div>

            {/* 🔥 GRÁFICOS — AGORA COM RECHARTS (GRElHA 2 COLUNAS) */}
            {!loading && produtos.length > 0 && (
                <div className="graficos-container">
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon">
                                <Icon name="inventory_2" size={24} color="#4f46e5" />
                            </div>
                            <div className="stat-info">
                                <span className="stat-value">{dadosGraficos.totalProdutos}</span>
                                <span className="stat-label">Total Produtos</span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <Icon name="category" size={24} color="#f59e0b" />
                            </div>
                            <div className="stat-info">
                                <span className="stat-value">{dadosGraficos.totalCategorias}</span>
                                <span className="stat-label">Categorias</span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <Icon name="paid" size={24} color="#10b981" />
                            </div>
                            <div className="stat-info">
                                <span className="stat-value">{formatarMoeda(dadosGraficos.valorTotalStock)}</span>
                                <span className="stat-label">Valor em Stock</span>
                            </div>
                        </div>
                    </div>

                    <div className="charts-grid">
                        {/* 🔥 Produtos por Categoria */}
                        <div className="chart-card">
                            <h4>Produtos por Categoria</h4>
                            <div className="chart-wrapper">
                                {dadosGraficos.categorias.length === 0 ? (
                                    <p className="chart-empty">Sem dados</p>
                                ) : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={dadosGraficos.categorias}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                            <Tooltip formatter={(v) => [v, 'Quantidade']} />
                                            <Bar dataKey="quantidade" radius={[6, 6, 0, 0]}>
                                                {dadosGraficos.categorias.map((_, i) => (
                                                    <Cell
                                                        key={i}
                                                        fill={CORES_GRAFICOS[i % CORES_GRAFICOS.length]}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>

                        {/* 🔥 Status de Stock (Doughnut → Pie com innerRadius) */}
                        <div className="chart-card">
                            <h4>Status de Stock</h4>
                            <div className="chart-wrapper">
                                {dadosGraficos.status.every(s => s.value === 0) ? (
                                    <p className="chart-empty">Sem dados</p>
                                ) : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie
                                                data={dadosGraficos.status}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="45%"
                                                outerRadius="70%"
                                                innerRadius="45%"
                                                paddingAngle={2}
                                                label={({ percent }) =>
                                                    percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''
                                                }
                                            >
                                                {dadosGraficos.status.map((_, i) => (
                                                    <Cell
                                                        key={i}
                                                        fill={CORES_STATUS_STOCK[i % CORES_STATUS_STOCK.length]}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend
                                                verticalAlign="bottom"
                                                iconType="circle"
                                                iconSize={8}
                                                wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>

                        {/* 🔥 Preço Médio por Categoria */}
                        <div className="chart-card">
                            <h4>Preço Médio por Categoria</h4>
                            <div className="chart-wrapper">
                                {dadosGraficos.precos.length === 0 ? (
                                    <p className="chart-empty">Sem dados</p>
                                ) : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={dadosGraficos.precos}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                            <YAxis
                                                tick={{ fontSize: 10 }}
                                                tickFormatter={(v) => v.toLocaleString('pt-PT')}
                                            />
                                            <Tooltip formatter={(v) => [formatarMoeda(v), 'Preço Médio']} />
                                            <Bar dataKey="preco" radius={[6, 6, 0, 0]}>
                                                {dadosGraficos.precos.map((_, i) => (
                                                    <Cell
                                                        key={i}
                                                        fill={CORES_GRAFICOS[(i + 3) % CORES_GRAFICOS.length]}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>

                        {/* 🔥 Stock por Categoria */}
                        <div className="chart-card">
                            <h4>Stock por Categoria</h4>
                            <div className="chart-wrapper">
                                {dadosGraficos.stock.length === 0 ? (
                                    <p className="chart-empty">Sem dados</p>
                                ) : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <LineChart data={dadosGraficos.stock}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                            <Tooltip formatter={(v) => [v, 'Quantidade']} />
                                            <Line
                                                type="monotone"
                                                dataKey="stock"
                                                stroke="#5c6bc0"
                                                strokeWidth={2}
                                                fill="rgba(92, 107, 192, 0.1)"
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 6 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MENSAGENS */}
            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={16} />
                    <span>{message.text}</span>
                    <button className="alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                </div>
            )}

            {/* FILTROS */}
            <div className="listar-produtos-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input
                        type="text"
                        placeholder="Pesquisar por nome, código, descrição, categoria ou fornecedor..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && aplicarFiltros()}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="filter-actions">
                    <button
                        className={`btn-filter ${showFiltros ? 'active' : ''}`}
                        onClick={() => setShowFiltros(!showFiltros)}
                    >
                        <Icon name="filter_list" size={18} />
                        {showFiltros ? 'Ocultar Filtros' : 'Filtros'}
                    </button>
                    <button className="btn-refresh" onClick={fetchProdutos} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                </div>
            </div>

            {/* FILTROS AVANÇADOS */}
            {showFiltros && (
                <div className="listar-produtos-filtros-avancados">
                    <div className="filtros-row">
                        <div className="filtro-group">
                            <label>Categoria</label>
                            <select
                                value={filtros.categoria}
                                onChange={(e) => setFiltros({ ...filtros, categoria: e.target.value })}
                            >
                                <option value="todos">Todas as Categorias</option>
                                {categorias.map(cat => (
                                    <option key={cat._id} value={cat._id}>{cat.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Fornecedor</label>
                            <select
                                value={filtros.fornecedor}
                                onChange={(e) => setFiltros({ ...filtros, fornecedor: e.target.value })}
                            >
                                <option value="todos">Todos os Fornecedores</option>
                                {fornecedores.map(f => (
                                    <option key={f._id} value={f._id}>{f.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Status Stock</label>
                            <select
                                value={filtros.statusStock}
                                onChange={(e) => setFiltros({ ...filtros, statusStock: e.target.value })}
                            >
                                <option value="todos">Todos</option>
                                <option value="sem_stock">🔴 Sem Stock</option>
                                <option value="baixo_stock">🟡 Stock Baixo</option>
                                <option value="stock_medio">🔵 Stock Médio</option>
                                <option value="stock_alto">🟢 Stock Alto</option>
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Unidade de Medida</label>
                            <select
                                value={filtros.unidadeMedida}
                                onChange={(e) => setFiltros({ ...filtros, unidadeMedida: e.target.value })}
                            >
                                <option value="todos">Todas</option>
                                {UNIDADES_MEDIDA.map(u => (
                                    <option key={u.value} value={u.value}>{u.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Status</label>
                            <select
                                value={filtros.ativo}
                                onChange={(e) => setFiltros({ ...filtros, ativo: e.target.value })}
                            >
                                <option value="todos">Todos</option>
                                <option value="ativo">✅ Ativos</option>
                                <option value="inativo">❌ Inativos</option>
                            </select>
                        </div>
                        <button className="btn-aplicar-filtros" onClick={aplicarFiltros}>
                            <Icon name="filter_list" size={16} /> Aplicar
                        </button>
                        <button className="btn-limpar-filtros" onClick={limparFiltros}>
                            <Icon name="clear" size={16} /> Limpar
                        </button>
                    </div>
                </div>
            )}

            {/* TABELA */}
            {loading ? (
                <div className="loading-container">
                    <div className="spinner-large"></div>
                    <p>A carregar produtos...</p>
                </div>
            ) : error ? (
                <div className="error-container">
                    <Icon name="error" size={48} />
                    <p>{error}</p>
                    <button className="btn-retry" onClick={fetchProdutos}>Tentar novamente</button>
                </div>
            ) : filteredProdutos.length === 0 ? (
                <div className="empty-state">
                    <Icon name="inventory_2" size={48} />
                    <p>Nenhum produto encontrado</p>
                    <p className="empty-subtext">Tente ajustar os filtros ou criar um novo produto</p>
                    <button className="btn-add-empty" onClick={() => setShowCreateModal(true)}>
                        <Icon name="add" size={16} /> Adicionar Produto
                    </button>
                </div>
            ) : (
                <div className="listar-produtos-table-container">
                    <table className="listar-produtos-table">
                        <thead>
                            <tr>
                                <th className="col-codigo">Código</th>
                                <th className="col-nome">Nome</th>
                                <th className="col-categoria">Categoria</th>
                                <th className="col-fornecedor">Fornecedor</th>
                                <th className="col-unidade">Unidade</th>
                                <th className="col-preco">Preço Venda</th>
                                <th className="col-stock">Stock</th>
                                <th className="col-status">Status</th>
                                <th className="col-acoes">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentProdutos.map(produto => {
                                const status = getStatusStock(produto.stockAtual, produto.stockMinimo);
                                const isAtivo = produto.ativo !== false;

                                return (
                                    <tr key={produto._id} className={!isAtivo ? 'inactive-row' : ''}>
                                        <td><span className="codigo-badge">{produto.codigo || '-'}</span></td>
                                        <td>
                                            <div className="produto-info">
                                                <strong>{produto.nome}</strong>
                                                {!isAtivo && (
                                                    <span className="status-badge inactive">Inativo</span>
                                                )}
                                                {produto.descricao && (
                                                    <span className="produto-descricao">{produto.descricao}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td><span className="produto-categoria">{produto.categoriaId?.nome || '-'}</span></td>
                                        <td><span className="produto-fornecedor">{produto.fornecedorId?.nome || '-'}</span></td>
                                        <td><span className="unidade-badge">{getUnidadeLabel(produto.unidadeMedida)}</span></td>
                                        <td className="preco-cell">{formatarMoeda(produto.precoVenda)}</td>
                                        <td>
                                            <span className={`stock-value ${status.class}`}>
                                                {formatarNumero(produto.stockAtual)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${status.class}`}>
                                                <Icon name={status.icon} size={12} />
                                                {status.label}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="acoes-container">
                                                <button
                                                    className="action-btn edit"
                                                    onClick={() => handleEdit(produto)}
                                                    title="Editar"
                                                >
                                                    <Icon name="edit" size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {renderPagination()}
                </div>
            )}
        </div>
    );
}